import { WiseOwlUser } from "./user";
import { ValueOf } from "./utility";
import { WiseOwlApplicationEnumType, WiseOwlIngress } from "./wiseOwlApi";
import { ZendeskSidebarTicket } from "./wiseOwlZendesk";

/**
 * EVENT TYPES
 */
export const WiseOwlEventEnum = {
  getChat: "getChat",
  getChatSummary: "getChatSummary",
  getPreChatContent: "getPreChatContent",
  liveChat: "liveChat",
  loginRedirect: "loginRedirect",
  phoneSupport: "phoneSupport",
  redirect: "redirect",
  sendChatMessage: "sendChatMessage",
  sendChatMessageRetry: "sendChatMessageRetry",
  startNewChat: "startNewChat",
  startNewChatRetry: "startNewChatRetry",
  streamComplete: "streamComplete",
  streamError: "streamError",
  streamInitialize: "streamInitialize",
  submitFeedback: "submitFeedback",
  submitTicket: "submitTicket",
};
export type WiseOwlEventEnumType = ValueOf<typeof WiseOwlEventEnum>;

export interface BaseEventData {
  chatId?: string;
}

export interface ChatEventData extends BaseEventData {
  redirectUrl?: never;
  retryId?: never;
  syncDocumentSid?: never;
  messageId?: never;
  positive?: never;
  resumeChat?: boolean;
}

export interface SyncDocumentEventData extends BaseEventData {
  syncDocumentSid: string;
  // Never
  messageId?: never;
  positive?: never;
  redirectUrl?: never;
  retryId?: never;
  resumeChat?: never;
}

export interface RedirectUrlEventData extends BaseEventData {
  redirectUrl: string;
  // Never
  messageId?: never;
  positive?: never;
  retryId?: never;
  syncDocumentSid?: never;
  resumeChat?: never;
}

export interface RetryEventData extends BaseEventData {
  retryId: string;
  // Nevers
  messageId?: never;
  positive?: never;
  redirectUrl?: never;
  syncDocumentSid?: never;
  resumeChat?: never;
}

export interface FeedbackEventData extends BaseEventData {
  messageId: string;
  positive: string;
  // Nevers
  redirectUrl?: never;
  retryId?: never;
  syncDocumentSid?: never;
  resumeChat?: never;
}

/**
 * EVENT RESPONSE TYPES
 */
export type OnEventResponse =
  | OnEventResponseGetChat
  | OnEventResponseGetChatSummary
  | OnEventResponseLiveChat
  | OnEventResponseLoginRedirect
  | OnEventResponsePhoneSupport
  | OnEventResponseRedirect
  | OnEventResponseSendChatMessage
  | OnEventResponseSendChatMessageRetry
  | OnEventResponseStartNewChat
  | OnEventResponseStartNewChatRetry
  | OnEventResponseStreamComplete
  | OnEventResponseStreamError
  | OnEventResponseStreamInitialize
  | OnEventResponseSubmitFeedback
  | OnEventResponseSubmitTicket;

export interface OnEventResponseGetChat {
  eventType: typeof WiseOwlEventEnum.getChat;
  data: ChatEventData;
}

export interface OnEventResponseGetChatSummary {
  eventType: typeof WiseOwlEventEnum.getChatSummary;
  data: ChatEventData;
}

// TODO: Update with live chat changes
export interface OnEventResponseLiveChat {
  eventType: typeof WiseOwlEventEnum.liveChat;
  data: ChatEventData;
}

export interface OnEventResponseLoginRedirect {
  eventType: typeof WiseOwlEventEnum.loginRedirect;
  data: ChatEventData;
}

// TODO: Update with phone support changes
export interface OnEventResponsePhoneSupport {
  eventType: typeof WiseOwlEventEnum.phoneSupport;
  data: ChatEventData;
}

export interface OnEventResponseRedirect {
  eventType: typeof WiseOwlEventEnum.redirect;
  data: RedirectUrlEventData;
}

export interface OnEventResponseSendChatMessage {
  eventType: typeof WiseOwlEventEnum.sendChatMessage;
  data: SyncDocumentEventData;
}

export interface OnEventResponseSendChatMessageRetry {
  eventType: typeof WiseOwlEventEnum.sendChatMessageRetry;
  data: RetryEventData;
}

export interface OnEventResponseStartNewChat {
  eventType: typeof WiseOwlEventEnum.startNewChat;
  data: SyncDocumentEventData;
}

export interface OnEventResponseStartNewChatRetry {
  eventType: typeof WiseOwlEventEnum.startNewChatRetry;
  data: RetryEventData;
}

export interface OnEventResponseStreamComplete {
  eventType: typeof WiseOwlEventEnum.streamComplete;
  data: ChatEventData;
}

export interface OnEventResponseStreamError {
  eventType: typeof WiseOwlEventEnum.streamError;
  data: ChatEventData;
}

export interface OnEventResponseStreamInitialize {
  eventType: typeof WiseOwlEventEnum.streamInitialize;
  data: ChatEventData;
}

export interface OnEventResponseSubmitFeedback {
  eventType: typeof WiseOwlEventEnum.submitFeedback;
  data: FeedbackEventData;
}

export interface OnEventResponseSubmitTicket {
  eventType: typeof WiseOwlEventEnum.submitTicket;
  data: ChatEventData;
}

/**
 * ERROR RESPONSE TYPES
 */
export type OnErrorResponse =
  | OnErrorResponseGetChatSummary
  | OnErrorResponseLiveChat
  | OnErrorResponseLoginRedirect
  | OnErrorResponsePhoneSupport
  | OnErrorResponseRedirect
  | OnErrorResponseSendChatMessage
  | OnErrorResponseSendChatMessageRetry
  | OnErrorResponseStartNewChat
  | OnErrorResponseStartNewChatRetry
  | OnErrorResponseStreamComplete
  | OnErrorResponseStreamError
  | OnErrorResponseStreamInitialize
  | OnErrorResponseSubmitFeedback
  | OnErrorResponseSubmitTicket;

export interface OnErrorResponseBase {
  error: Error;
}

export type OnErrorResponseGetChatSummary = OnErrorResponseBase &
  OnEventResponseGetChatSummary;
export type OnErrorResponseLiveChat = OnErrorResponseBase &
  OnEventResponseLiveChat;
export type OnErrorResponseLoginRedirect = OnErrorResponseBase &
  OnEventResponseLoginRedirect;
export type OnErrorResponsePhoneSupport = OnErrorResponseBase &
  OnEventResponsePhoneSupport;
export type OnErrorResponseRedirect = OnErrorResponseBase &
  OnEventResponseRedirect;
export type OnErrorResponseSendChatMessage = OnErrorResponseBase &
  OnEventResponseSendChatMessage;
export type OnErrorResponseSendChatMessageRetry = OnErrorResponseBase &
  OnEventResponseSendChatMessageRetry;
export type OnErrorResponseStartNewChat = OnErrorResponseBase &
  OnEventResponseStartNewChat;
export type OnErrorResponseStartNewChatRetry = OnErrorResponseBase &
  OnEventResponseStartNewChatRetry;
export type OnErrorResponseStreamComplete = OnErrorResponseBase &
  OnEventResponseStreamComplete;
export type OnErrorResponseStreamError = OnErrorResponseBase &
  OnEventResponseStreamError;
export type OnErrorResponseStreamInitialize = OnErrorResponseBase &
  OnEventResponseStreamInitialize;
export type OnErrorResponseSubmitFeedback = OnErrorResponseBase &
  OnEventResponseSubmitFeedback;
export type OnErrorResponseSubmitTicket = OnErrorResponseBase &
  OnEventResponseSubmitTicket;

/**
 * TRACK EVENT RESPONSE TYPES
 */
export interface OnTrackEventResponse {
  eventName: string;
  additionalData?: Record<string, unknown>;
}

/**
 * WISE OWL PROPS
 */
export interface WiseOwlBaseProps {
  /**
   * Display name of the assistant to use for the chat (e.g. "Help Center Assistant", "Support Co-Pilot", "Intelligent Discovery")
   */
  displayName?: string;
  chatId?: string;
  onError?: (args: OnErrorResponse) => void;
  onEvent?: (args: OnEventResponse) => void;
  onTrackEvent?: (args: OnTrackEventResponse) => void;
  user: WiseOwlUser;
}

export interface WiseOwlConsoleProps extends WiseOwlBaseProps {
  applicationId: WiseOwlApplicationEnumType;
  ingress: WiseOwlIngress.console;
  // Nevers
  stargateUngatedUserId?: never;
  ticket?: never;
  token?: never;
}

export interface WiseOwlOscAuthenticatedProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.osc;
  // Nevers
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
  token?: never;
}

export interface WiseOwlOscUnauthenticatedProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.oscUngated;
  stargateUngatedUserId: string;
  // Nevers
  applicationId?: never;
  ticket?: never;
  token?: never;
}

export interface WiseOwlSalesforceProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.salesforce;
  // Nevers
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
  token?: never;
}

export interface WiseOwlZendeskProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.zendesk;
  ticket: ZendeskSidebarTicket;
  token: string;
  // Nevers
  applicationId?: never;
  stargateUngatedUserId?: never;
}

export type WiseOwlProps =
  | WiseOwlConsoleProps
  | WiseOwlOscAuthenticatedProps
  | WiseOwlOscUnauthenticatedProps
  | WiseOwlSalesforceProps
  | WiseOwlZendeskProps;
